/**
 * Verrou « code parent » : l'appareil peut être dans les mains de l'enfant,
 * donc l'espace adulte (réglages, création, abonnement) demande le code
 * quand il y en a un (`me.kid_pin_set`).
 *
 * L'état déverrouillé ne vit qu'en mémoire (contexte React) : jamais dans
 * localStorage, un rechargement redemande le code. Il expire de lui-même
 * après `PARENT_UNLOCK_TTL_MS`, et tombe tout de suite quand la page passe
 * en arrière-plan (écran verrouillé, changement d'appli) : rendre le
 * téléphone à l'enfant reverrouille donc l'espace parent.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { useAuth } from "./auth";

export const PARENT_UNLOCK_TTL_MS = 10 * 60 * 1000;

/** `unlockedAt` = instant du déverrouillage (ms), ou null s'il est verrouillé. */
export function isParentUnlocked(unlockedAt: number | null, now: number): boolean {
  return unlockedAt !== null && now - unlockedAt < PARENT_UNLOCK_TTL_MS;
}

/** Nouvel état après un changement de visibilité : masquée => reverrouillé. */
export function unlockedAtAfterVisibility(
  unlockedAt: number | null,
  visibility: DocumentVisibilityState,
): number | null {
  return visibility === "hidden" ? null : unlockedAt;
}

interface ParentGateApi {
  unlocked: boolean;
  unlock: () => void;
}

const ParentGateCtx = createContext<ParentGateApi | null>(null);

export function ParentGateProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [unlockedAt, setUnlockedAt] = useState<number | null>(null);

  const unlock = useCallback(() => setUnlockedAt(Date.now()), []);

  useEffect(() => {
    function onVisibilityChange() {
      setUnlockedAt((at) => unlockedAtAfterVisibility(at, document.visibilityState));
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Une minuterie plutôt qu'un test au rendu : l'expiration doit refermer
  // l'écran même si rien d'autre ne le fait re-rendre.
  useEffect(() => {
    if (unlockedAt === null) return;
    const remaining = Math.max(0, PARENT_UNLOCK_TTL_MS - (Date.now() - unlockedAt));
    const timer = window.setTimeout(() => setUnlockedAt(null), remaining);
    return () => window.clearTimeout(timer);
  }, [unlockedAt]);

  // Déconnexion : le prochain compte sur cet appareil ne doit rien hériter.
  useEffect(() => {
    if (!isAuthenticated) setUnlockedAt(null);
  }, [isAuthenticated]);

  const api = useMemo<ParentGateApi>(
    () => ({ unlocked: isParentUnlocked(unlockedAt, Date.now()), unlock }),
    [unlockedAt, unlock],
  );

  return <ParentGateCtx.Provider value={api}>{children}</ParentGateCtx.Provider>;
}

export function useParentGate(): ParentGateApi {
  const ctx = useContext(ParentGateCtx);
  if (!ctx) throw new Error("useParentGate must be used inside ParentGateProvider");
  return ctx;
}
